import { useState } from 'react'
import { FiFlag, FiX, FiLoader } from 'react-icons/fi'
import axios from 'axios'
import toast from 'react-hot-toast'
import { isFormStylingApplied, isButtonStylingApplied } from '../../utils/sectionFormStyling'

export default function ReportContentModal({ isOpen, onClose, website, sectionId, sectionFormStyling, useDefaultStyling = true, sectionType = 'report' }) {
  const [reportType, setReportType] = useState('illegal-content')
  const [description, setDescription] = useState('')
  const [reporterName, setReporterName] = useState('')
  const [reporterEmail, setReporterEmail] = useState('')
  const isFormStyling = useDefaultStyling || isFormStylingApplied(sectionFormStyling, sectionType, sectionId)
  const isButtonStyling = useDefaultStyling || isButtonStylingApplied(sectionFormStyling, sectionType, sectionId)
  
  // Default values to fallback to if styling is not provided
  const DEFAULT_FORM_COLORS = {
    backgroundColor: '#FFFFFF',
    textColor: '#111827',
    hoverBackgroundColor: '#F9FAFB',
    hoverTextColor: '#374151',
    buttonBackgroundColor: '#3B82F6',
    buttonTextColor: '#FFFFFF',
    hoverButtonBackgroundColor: '#2563eb',
    hoverButtonTextColor: '#ffffff'
  }
  
  // Ensure we have valid styling values
  const formColors = isFormStyling ? (sectionFormStyling || DEFAULT_FORM_COLORS) : undefined
  
  const formStyle = isFormStyling ? {
    backgroundColor: formColors?.backgroundColor || DEFAULT_FORM_COLORS.backgroundColor,
    color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor
  } : undefined
  const labelStyle = isFormStyling ? { color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor } : undefined
  const textStyle = isFormStyling ? { color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor } : undefined
  const buttonStyle = isButtonStyling ? {
    backgroundColor: formColors?.buttonBackgroundColor || DEFAULT_FORM_COLORS.buttonBackgroundColor,
    color: formColors?.buttonTextColor || DEFAULT_FORM_COLORS.buttonTextColor
  } : { backgroundColor: '#3B82F6', color: '#FFFFFF' } // Always provide default button styling
  const [loading, setLoading] = useState(false)

  if (!isOpen) return null

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!description.trim()) {
      alert('Please describe the issue')
      return
    }

    setLoading(true)
    try {
      await axios.post(`/websites-public/${website.slug}/report`, {
        reportType,
        description,
        reporterName: reporterName || 'Anonymous',
        reporterEmail: reporterEmail || null,
        sectionId
      })
      toast.success('Report submitted successfully. Thank you for helping us keep the platform safe.')
      setDescription('')
      setReporterName('')
      setReporterEmail('')
      setReportType('illegal-content')
      onClose()
    } catch (error) {
      console.error('Error submitting report:', error)
      toast.error(error.response?.data?.message || 'Failed to submit report')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold" style={textStyle}>Report Content</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FiX className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4" style={formStyle}>
          <div>
            <label className="block text-sm font-medium mb-2" style={labelStyle}>
              Report Type
            </label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              style={{backgroundColor: '#FFFFFF', color: '#111827', borderColor: '#111827'}}
              className="input-field"
            >
              <option value="illegal-content">Illegal Content</option>
              <option value="abusive-content">Abusive Content</option>
              <option value="spam">Spam</option>
              <option value="misinformation">Misinformation</option>
              <option value="copyright-infringement">Copyright Infringement</option>
              <option value="explicit-content">Explicit Content</option>
              <option value="harassment">Harassment</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={labelStyle}>
              Description *
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please describe the issue in detail..."
              style={{backgroundColor: '#FFFFFF', color: '#111827', borderColor: '#111827'}}
              className="input-field"
              rows="4"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={labelStyle}>
              Your Name (Optional)
            </label>
            <input
              type="text"
              value={reporterName}
              onChange={(e) => setReporterName(e.target.value)}
              placeholder="Your name (anonymous if left blank)"
              style={{backgroundColor: '#FFFFFF', color: '#111827', borderColor: '#111827'}}
              className="input-field"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={labelStyle}>
              Your Email (Optional)
            </label>
            <input
              type="email"
              value={reporterEmail}
              onChange={(e) => setReporterEmail(e.target.value)}
              placeholder="your@email.com"
              style={{backgroundColor: '#FFFFFF', color: '#111827', borderColor: '#111827'}}
              className="input-field"
            />
            <p className="text-xs mt-1" style={textStyle}>We may contact you about the status of your report</p>
          </div>

          <div className="bg-blue-50 p-3 rounded-lg text-xs text-blue-700">
            Your report helps us keep the platform safe. All reports are treated confidentially and reviewed by our moderation team.
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={buttonStyle}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={buttonStyle}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <FiLoader className="w-4 h-4 animate-spin" /> Submitting...
                </>
              ) : (
                <>
                  <FiFlag className="w-4 h-4" /> Submit Report
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
